/**
 * Preflight for bank quizzes: pick count, domains, practice vs timed.
 */
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Play } from 'lucide-react';
import { testIds } from '@/lib/test-ids';
import type { QuizQuestion } from '@/lib/quiz-parser';
import { cn } from '@/lib/utils';

export type SessionBuilderMode = 'practice' | 'timed';

export interface SessionBuilderConfig {
  mode: SessionBuilderMode;
  count: number;
  domains: string[];
  verifiedOnly: boolean;
  excludeMock: boolean;
  mix: 'random' | 'weighted';
}

interface QuizSessionBuilderProps {
  questions: QuizQuestion[];
  defaultCount?: number;
  domainWeights?: Record<string, number>;
  excludeMockLabel?: string;
  onStart: (config: SessionBuilderConfig) => void;
  starting?: boolean;
}

const COUNT_PRESETS = [5, 10, 20, 50];

function formatDomain(domain: string) {
  return domain.replace(/_/g, ' ');
}

export function QuizSessionBuilder({
  questions,
  defaultCount = 20,
  domainWeights,
  excludeMockLabel = 'Exclude dress rehearsal questions',
  onStart,
  starting = false,
}: QuizSessionBuilderProps) {
  const domains = useMemo(() => {
    const set = new Set<string>();
    for (const q of questions) {
      if (q.domain) set.add(q.domain);
    }
    return [...set].sort();
  }, [questions]);

  const [mode, setMode] = useState<SessionBuilderMode>('practice');
  const [count, setCount] = useState(String(defaultCount));
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [verifiedOnly, setVerifiedOnly] = useState(true);
  const [excludeMock, setExcludeMock] = useState(true);
  const [mix, setMix] = useState<'random' | 'weighted'>('random');

  const toggleDomain = (domain: string) => {
    setSelectedDomains((prev) =>
      prev.includes(domain) ? prev.filter((d) => d !== domain) : [...prev, domain],
    );
  };

  const poolSize = useMemo(() => {
    let pool = questions;
    if (verifiedOnly) {
      pool = pool.filter((q) => {
        const s = String((q as QuizQuestion & { status?: string }).status || 'verified').toLowerCase();
        return s !== 'draft' && s !== 'retired';
      });
    }
    if (selectedDomains.length > 0) {
      const want = new Set(selectedDomains);
      pool = pool.filter((q) => want.has(q.domain));
    }
    return pool.length;
  }, [questions, verifiedOnly, selectedDomains]);

  const countNum = Math.min(Math.max(1, Number(count) || defaultCount), poolSize || 1);

  return (
    <div
      className="flex-1 min-h-0 flex flex-col w-full max-w-3xl mx-auto"
      data-testid={testIds.quizDocumentRenderer.sessionBuilder}
    >
      <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-6 pb-4 space-y-5">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 bg-teal-500/15 text-teal-600">
            <Play className="h-5 w-5" />
          </div>
          <div className="min-w-0 space-y-1">
            <h3 className="text-lg font-semibold">Session builder</h3>
            <p className="text-sm text-muted-foreground">
              {questions.length} questions in bank · {poolSize} match filters
            </p>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Mode</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant={mode === 'practice' ? 'default' : 'outline'}
                onClick={() => setMode('practice')}
                data-testid={testIds.quizDocumentRenderer.sessionModePractice}
              >
                Practice
              </Button>
              <Button
                type="button"
                size="sm"
                variant={mode === 'timed' ? 'default' : 'outline'}
                onClick={() => setMode('timed')}
                data-testid={testIds.quizDocumentRenderer.sessionModeTimed}
              >
                Timed
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Question count</Label>
            <Select value={count} onValueChange={setCount}>
              <SelectTrigger data-testid={testIds.quizDocumentRenderer.sessionCount}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNT_PRESETS.filter((n) => n <= Math.max(poolSize, 5)).map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
                {poolSize > 0 && !COUNT_PRESETS.includes(poolSize) && (
                  <SelectItem value={String(poolSize)}>All matching ({poolSize})</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {domainWeights && Object.keys(domainWeights).length > 0 && (
            <div className="space-y-2">
              <Label>Mix</Label>
              <Select value={mix} onValueChange={(v) => setMix(v as 'random' | 'weighted')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="random">Random</SelectItem>
                  <SelectItem value="weighted">Exam-weighted</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2 sm:col-span-2">
            <div className="flex items-center justify-between gap-2">
              <Label>Domains (empty = all)</Label>
              {selectedDomains.length > 0 ? (
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setSelectedDomains([])}
                >
                  Clear ({selectedDomains.length})
                </button>
              ) : null}
            </div>
            <div className="max-h-28 overflow-y-auto rounded-md border border-border bg-muted/20 p-2">
              <div className="flex flex-wrap gap-1.5">
                {domains.map((domain) => {
                  const on = selectedDomains.includes(domain);
                  return (
                    <button
                      key={domain}
                      type="button"
                      onClick={() => toggleDomain(domain)}
                      className={cn(
                        'text-[11px] leading-tight px-1.5 py-0.5 rounded border transition-colors',
                        on
                          ? 'bg-teal-500/15 border-teal-500/40 text-teal-700 dark:text-teal-300'
                          : 'bg-background/60 border-border text-muted-foreground',
                      )}
                      data-testid={testIds.quizDocumentRenderer.sessionDomain(domain)}
                    >
                      {formatDomain(domain)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
            <Label htmlFor="verified-only" className="text-sm">
              Verified only
            </Label>
            <Switch
              id="verified-only"
              checked={verifiedOnly}
              onCheckedChange={setVerifiedOnly}
              data-testid={testIds.quizDocumentRenderer.sessionVerifiedOnly}
            />
          </div>

          <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
            <Label htmlFor="exclude-mock" className="text-sm">
              {excludeMockLabel}
            </Label>
            <Switch
              id="exclude-mock"
              checked={excludeMock}
              onCheckedChange={setExcludeMock}
              data-testid={testIds.quizDocumentRenderer.sessionExcludeMock}
            />
          </div>
        </div>
      </div>

      <div className="shrink-0 border-t border-border px-6 py-4 bg-background/95">
        <Button
          className="w-full"
          disabled={starting || poolSize === 0}
          data-testid={testIds.quizDocumentRenderer.startQuiz}
          onClick={() =>
            onStart({
              mode,
              count: countNum,
              domains: selectedDomains,
              verifiedOnly,
              excludeMock,
              mix,
            })
          }
        >
          {starting ? 'Starting...' : mode === 'practice' ? 'Start practice' : 'Start timed quiz'}
        </Button>
      </div>
    </div>
  );
}
